import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type AdminComment,
  adminService,
  type ContentForReview,
  type ContentListItem,
  type ListContentParams,
  type UpdateContentDto,
} from '@/api/services';

const PAGE_SIZE = 30;

export function useContentList(params?: ListContentParams) {
  return useQuery({
    queryKey: ['admin', 'content', 'list', params],
    queryFn: () => adminService.listContent(params),
    staleTime: 30 * 1000, // 30 seconds
  });
}

export function useInfiniteContentList(params?: Omit<ListContentParams, 'page' | 'limit'>) {
  return useInfiniteQuery({
    queryKey: ['admin', 'content', 'infinite', params],
    queryFn: ({ pageParam = 1 }) => {
      const fullParams = { ...params, page: pageParam, limit: PAGE_SIZE };
      return adminService.listContent(fullParams);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const currentTotal = allPages.reduce((acc, page) => acc + page.items.length, 0);
      if (currentTotal >= lastPage.total) {
        return undefined; // No more pages
      }
      return allPages.length + 1;
    },
    staleTime: 30 * 1000, // 30 seconds
  });
}

// Helper to flatten infinite query pages into a single items array
export function flattenInfinitePages(
  pages: Array<{ items: ContentListItem[]; total: number }> | undefined,
): { items: ContentListItem[]; total: number } {
  if (!pages || pages.length === 0) {
    return { items: [], total: 0 };
  }
  const items = pages.flatMap((page) => page.items);
  const total = pages[0].total;
  return { items, total };
}

export function useContentForReview(id: string | null) {
  return useQuery({
    queryKey: ['admin', 'content', id],
    queryFn: () => adminService.getContent(id!),
    enabled: !!id,
    staleTime: 60 * 1000, // 1 minute
  });
}

export function useTags() {
  return useQuery({
    queryKey: ['admin', 'tags'],
    queryFn: () => adminService.getTags(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useUpdateContent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateContentDto }) =>
      adminService.updateContent(id, updates),
    onSuccess: (data: ContentForReview) => {
      // Update the specific content cache
      queryClient.setQueryData(['admin', 'content', data.id], data);
      // Invalidate queries to force refetch with fresh data
      queryClient.invalidateQueries({ queryKey: ['admin', 'content', data.id] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'content', 'list'] });
    },
  });
}

export function useApproveContent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminService.approveContent(id),
    onSuccess: (_data, id) => {
      // Invalidate queries to refresh content
      queryClient.invalidateQueries({ queryKey: ['admin', 'content', id] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'content', 'list'] });
    },
  });
}

export function useRejectContent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminService.rejectContent(id),
    onSuccess: (_data, id) => {
      // Invalidate queries to refresh content
      queryClient.invalidateQueries({ queryKey: ['admin', 'content', id] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'content', 'list'] });
    },
  });
}

export function useToggleFlag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminService.toggleFlag(id),
    onSuccess: (_data, id) => {
      // Invalidate queries to refresh content
      queryClient.invalidateQueries({ queryKey: ['admin', 'content', id] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'content', 'list'] });
    },
  });
}

export function useTranslateContent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminService.translateContent(id),
    onSuccess: () => {
      // A new pt content record was created — invalidate the list so it shows up
      queryClient.invalidateQueries({ queryKey: ['admin', 'content', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'content', 'infinite'] });
    },
  });
}

export function useBulkApprove() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => adminService.bulkApprove(ids),
    onSuccess: () => {
      // Invalidate list to refresh content
      queryClient.invalidateQueries({ queryKey: ['admin', 'content', 'list'] });
    },
  });
}

export function useBulkReject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => adminService.bulkReject(ids),
    onSuccess: () => {
      // Invalidate list to refresh content
      queryClient.invalidateQueries({ queryKey: ['admin', 'content', 'list'] });
    },
  });
}

// Comments hooks
export function useAdminComments(contentId: string | null) {
  return useQuery({
    queryKey: ['admin', 'comments', contentId],
    queryFn: () => adminService.getAdminComments(contentId!),
    enabled: !!contentId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

export function useCreateAdminComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contentId, content }: { contentId: string; content: string }) =>
      adminService.createAdminComment(contentId, content),
    onSuccess: (newComment: AdminComment, { contentId }) => {
      // Optimistically add the comment to the cache
      queryClient.setQueryData<AdminComment[]>(['admin', 'comments', contentId], (old = []) => [
        ...old,
        newComment,
      ]);
    },
  });
}

export function useDeleteAdminComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ commentId }: { commentId: string; contentId: string }) =>
      adminService.deleteAdminComment(commentId),
    onSuccess: (_, { commentId, contentId }) => {
      // Optimistically remove the comment from the cache
      queryClient.setQueryData<AdminComment[]>(['admin', 'comments', contentId], (old = []) =>
        old.filter((c) => c.id !== commentId),
      );
    },
  });
}

export function useNewsletterSendPreview(id: string | null) {
  return useQuery({
    queryKey: ['admin', 'newsletter-sends', id],
    queryFn: () => adminService.getNewsletterSendPreview(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Helper to get the latest revision data (AI or HUMAN)
export function getLatestRevisionBySource(
  revisions: ContentForReview['revisions'],
  source: 'AI' | 'HUMAN',
) {
  const filtered = revisions.filter((r) => r.source === source);
  if (filtered.length === 0) return null;
  return filtered[filtered.length - 1];
}

// Helper to check if content has been modified by human
export function hasHumanRevision(revisions: ContentForReview['revisions']) {
  return revisions.some((r) => r.source === 'HUMAN');
}

// Helper to get diff between AI and current values
export function getDiff(
  content: ContentForReview,
): Array<{ field: string; ai: string | undefined; current: string | undefined }> {
  const aiRevision = getLatestRevisionBySource(content.revisions, 'AI');
  if (!aiRevision) return [];

  const diffs: Array<{ field: string; ai: string | undefined; current: string | undefined }> = [];

  // Compare each field
  if (aiRevision.data.title !== undefined && aiRevision.data.title !== content.title) {
    diffs.push({ field: 'title', ai: aiRevision.data.title, current: content.title ?? undefined });
  }
  if (aiRevision.data.summary !== undefined && aiRevision.data.summary !== content.summary) {
    diffs.push({
      field: 'summary',
      ai: aiRevision.data.summary,
      current: content.summary ?? undefined,
    });
  }
  if (aiRevision.data.category !== undefined && aiRevision.data.category !== content.category) {
    diffs.push({
      field: 'category',
      ai: aiRevision.data.category,
      current: content.category ?? undefined,
    });
  }
  if (aiRevision.data.author !== undefined && aiRevision.data.author !== content.author) {
    diffs.push({
      field: 'author',
      ai: aiRevision.data.author,
      current: content.author ?? undefined,
    });
  }

  return diffs;
}
